/**
 * Cache service for API responses to reduce backend load
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number; // Time to live in milliseconds
}

class CacheService {
  private cache = new Map<string, CacheEntry<any>>();
  
  // Default TTL values (in milliseconds)
  private readonly DEFAULT_TTL = {
    suggestions: 5 * 60 * 1000,    // 5 minutes
    employees: 10 * 60 * 1000,     // 10 minutes
    restaurants: 15 * 60 * 1000,   // 15 minutes
    expenses: 2 * 60 * 1000,       // 2 minutes
    kpi: 5 * 60 * 1000,            // 5 minutes
    default: 5 * 60 * 1000         // 5 minutes
  };

  /**
   * Get cached data if it exists and is not expired
   */
  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    
    if (!entry) {
      return null;
    }
    
    const now = Date.now();
    if (now - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }
    
    return entry.data;
  }

  /**
   * Set cache entry with TTL
   */
  set<T>(key: string, data: T, ttl?: number): void {
    const entryTTL = ttl || this.DEFAULT_TTL.default;
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: entryTTL
    });
  }

  /**
   * Generate cache key for API endpoints
   */
  generateKey(endpoint: string, params?: Record<string, any>): string {
    if (!params || Object.keys(params).length === 0) {
      return endpoint;
    }
    
    const sortedParams = Object.keys(params)
      .sort()
      .map(key => `${key}=${params[key]}`)
      .join('&');
    
    return `${endpoint}?${sortedParams}`;
  }

  /**
   * Clear specific cache entry
   */
  clear(key: string): void {
    this.cache.delete(key);
  }

  /**
   * Clear all cache entries
   */
  clearAll(): void {
    this.cache.clear();
  }

  /**
   * Clear expired entries
   */
  clearExpired(): void {
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp > entry.ttl) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Get cache statistics
   */
  getStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }

  /**
   * Check if we should skip API call for empty responses
   */
  shouldSkipEmptyRequest(key: string, emptyThreshold: number = 30000): boolean {
    const entry = this.cache.get(key);
    if (!entry) return false;
    
    const now = Date.now();
    const timeSinceLastRequest = now - entry.timestamp;
    
    // If last request was recent and returned empty data, skip
    if (timeSinceLastRequest < emptyThreshold && 
        (Array.isArray(entry.data) && entry.data.length === 0)) {
      return true;
    }
    
    return false;
  }
}

export default new CacheService();
